import React, {
  useRef,
  useCallback,
  useContext,
  useEffect
} from "react";
import { BryntumSchedulerPro } from "@bryntum/schedulerpro-react";
import { PLAN_VIEW_CONFIG } from "../../other/constants/PlanView/ViewConfg.jsx";
import BryntumContext, { getSetting, userPreferences } from "../context/bryntumContext.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import {
  swivuCheckGridGrants,
  getOriginalRecord, disableZoomTimeline
} from "../../other/utils/utils.jsx";
import {
  callOnLoadOnColumns,
  canOpenEditor, getScheduleInstanceFromViewManager,
  onTaskGridColumnDrop
} from "../../other/utils/otherUtils.jsx";
import useRefsInit from "../hooks/useRefsInit.jsx";
import {GRANTS} from "../../other/constants/PrivilegeTypes.jsx";
import AuthContext from "../context/authContext.jsx";
import { processCellMenuItems } from "../../other/utils/gridCellMenuUtils.jsx";
import SwivuSearchBar from "../components/SwivuSearchBar.jsx";
import { VIEW_TYPES, ViewType } from "../../other/constants/OtherKeys.jsx";
import AlertNotificationBanner from "../components/alertNotificationBanner/AlertNotificationBanner.jsx";
import {StringHelper} from "@bryntum/schedulerpro";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../other/constants/PersistenceStateKeys.jsx";
import SwivuSchedulerTimelineSortPlugin from "../../bryntum-lib/plugins/SwivuSchedulerTimelineSortPlugin.jsx";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import { WidgetParamKeys } from "@/types/entity";

const   PlanPage = () => {
  const planViewRef = useRef(null);
  const { setRefs, isRefsInit } = useRefsInit([planViewRef]);
  const { user } = useContext(AuthContext);
  const context = useContext(BryntumContext);
  const {
    project,
    planProject,
    stateProvider,
    editorHandler,
    currentViewRef,
    viewManager,
    entityManager
  } = context;
  useEnableViewTracking(planViewRef, VIEW_TYPES.tasks, isRefsInit, VIEW_TYPES.planView);

  const editorHandlerRef = useRef();
  editorHandlerRef.current = editorHandler;

  const setEventLayout = useCallback(() => {
    if (planViewRef.current) {
      const viewType = getSetting("viewType");
      planViewRef.current.instance.eventLayout = viewType === ViewType.compact || viewType === ViewType.compressed ? "pack": "stack";
    }
  }, []);

  // install listener on userPreferences
  useEffect(() => {
    setEventLayout();
    const zoomOnMousePrefValue = StringHelper.safeJsonParse(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.disableTimelineZoomOnMouse, true));
    disableZoomTimeline(planViewRef.current.instance, zoomOnMousePrefValue);
    userPreferences.boardPreferencesStore.on("change", ({ record }) => {
      if (record?.key === "taskColor" || record?.key === "taskStyle") {
        planViewRef.current.instance.refreshRows();
      }
    }, planViewRef.current.instance);

    /* Handling user preference for event layout here instead of swivuSchedulerPro, since event layout setting is exclusive for plan page  */
    userPreferences.on("change", ({ record }) => {
      if (record?.key === "viewType") setEventLayout();
    }, planViewRef.current?.instance);
  }, []);

  useEffect(() => {
    if (planProject && stateProvider) {
      const planCurrentInstance = planViewRef.current?.instance;

      planCurrentInstance.viewManager = viewManager;
      if (!planCurrentInstance.hasPlugin(SwivuSchedulerTimelineSortPlugin)) planCurrentInstance.addPlugins(SwivuSchedulerTimelineSortPlugin);

      planViewRef.current.instance.features.cellMenu.processItems =
        processCellMenuItems({ grid: planCurrentInstance, promptOnRemove: true });
      planCurrentInstance.project = planProject;
      planCurrentInstance.store.relayAll(
        planViewRef.current?.instance,
        "swivuTaskStore"
      );

      const createEventRecord = planCurrentInstance.features.eventDragCreate.createEventRecord.bind(planCurrentInstance.features.eventDragCreate);
      planCurrentInstance.features.eventDragCreate.createEventRecord = function(d) {
        const event = createEventRecord(d);
        event.eventStyle = "dashed";
        event.eventColor = "red";
        event.isPlanningDateEvent = true;
        event.name = ""
        return event;
      }

      planCurrentInstance.onClickSwivuEditMessage = () => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings' });
      }
      swivuCheckGridGrants(planCurrentInstance, user);
      planCurrentInstance.enableDeleteKey = false;
      callOnLoadOnColumns(planCurrentInstance.columns, planProject, { editorHandler, user, stateProvider, currentViewRef, entityManager, viewManager });

      currentViewRef.current.scrollSchedulerTimeAxisToCurrDate = () => {
        const schedulerView = getScheduleInstanceFromViewManager(viewManager);
        if (schedulerView && userPreferences) {
          const isCenterTimelineOnToday = StringHelper.safeJsonParse(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.centerSchedulerLineOnToday, true))
          schedulerView.scrollToNow({
            animate: true,
            block: isCenterTimelineOnToday ? "center": "start",
          });
          currentViewRef.current.swivuLastViewportCenterDate = planCurrentInstance.viewportCenterDate;
        }
      };
    }
  }, [
    planViewRef,
    planProject,
    stateProvider,
    currentViewRef,
    user,
    editorHandler
  ]);

  /* Keeping these clean up function in effects with other dependencies causes the ref to reset so any editors using it faces causes null, therefore keeping it here */
  useEffect(() => {
    return () => {
      currentViewRef.current.swivuLastViewportCenterDate = null;
    }
  }, [currentViewRef]);


  const showTaskEditor = useCallback((e) => {
    const { record, assignmentRecord, column, isTimelineContext } = e;
    // plans view -> double-click on event doesn't have required properties such as column and record
    if (isTimelineContext || (record && !canOpenEditor(column, record))) return;
    if (project) {
      const taskId = record ? getOriginalRecord(record).id : assignmentRecord.resourceId;
      const task = project.extraTaskStore.getById(taskId);
      if (column.isBudgetAccountColumn) {
        if (task.budgetId && user.can(GRANTS.grant_two)) {
          editorHandler.openEditor(EDITOR_KEYS.budgetEditor, task.budget, {
          budget: task.budget
        })
      }
    } else {
      entityManager.openEntityManager({entityType: ENTITY_TYPES.TASK, entityId: task.id});
      }
    }
  }, [entityManager]);

  const canResizeOrDragEvent = (e) => {
    return Boolean(e.eventRecord.isPlanningDateEvent);
  }

  const canDropEvent = ({ context }) => {
    // prevent switching the resource of Planning Date event
    context.valid = Boolean(context.resourceRecord.id === context.newResource.id);
  }

  const onEventDragEndHandler = (e) => {
    const eventRecord = e.eventRecord || e.eventRecords[0];
    if (eventRecord) {
      const planningDateRecordId = eventRecord.planningDateId;
      const planningDateRecord = project.taskPlanningDatesStore.getById(planningDateRecordId);
      planningDateRecord.set({
        startDate: eventRecord.startDate,
        endDate: eventRecord.endDate,
      });
    }
  }

  const onCreateNewTaskEventByDragCreate = useCallback(({resourceRecord, context, event}) => {
    const taskRecord = getOriginalRecord(resourceRecord);

    context.async = true;
    context.finalize(false); // Prevent the default bryntum's event record creation

    if (event.shiftKey) {
      entityManager.openEntityManager({entityType: ENTITY_TYPES.TASK, entityId: taskRecord.id, otherParams: {
        [WidgetParamKeys.PLANNING.CREATE]: {
          startDate: context.startDate,
          endDate: context.endDate,
        }
      }});
    } else {
      /* When using context.finalize(false) it prevents immediate records from being painted on the scheduler, This way our task planning record was not being painted on the scheduler as therefore we are using this setTimeout to create the record after callstack is cleared. */
      setTimeout(() => {
        const { startDate, endDate } = context;
        const planningDateRecord = project.taskPlanningDatesStore.createRecord({
          task: taskRecord.id,
          name: "",
          startDate,
          endDate,
          color: "red"
        });
        project.taskPlanningDatesStore.add(planningDateRecord); // add record to store
      })
    }
  }, [editorHandler, project]);

  return (
    <>
      <SwivuSearchBar searchableRefs={[planViewRef]} />
      {user && (
        <AlertNotificationBanner project={project} editorHandler={editorHandler} stateProvider={stateProvider} user={user} />
      )}
      <BryntumSchedulerPro
        onColumnDrop={onTaskGridColumnDrop(["left"])}
        ref={setRefs(planViewRef)}
        {...PLAN_VIEW_CONFIG}
        disabled={user && !user.can(GRANTS.grant_four)}
        stateId
        flex="1 1 75%"
        onScheduleDblClick={() => false} // Bryntum v7: Prevent double-click event creation
        onCellDblClick={showTaskEditor}
        onEventDblClick={({ eventRecord }) => {
          const taskRecord = getOriginalRecord(eventRecord.resource);
          const task = project.extraTaskStore.getById(taskRecord.id);
          if (taskRecord) {
            entityManager.openEntityManager({entityType: ENTITY_TYPES.TASK, entityId: task.id, otherParams: {
              [WidgetParamKeys.PLANNING.EDIT]: eventRecord.isPlanningDateEvent ? eventRecord.planningDateId : undefined
            }});
          }
        }}
        sortFeature={{
          multiSort: false,
        }}
        onMouseOver={() => {
          // See SwivuHeaderBottomTier.jsx tickSize change handler for more info
          currentViewRef.current.swivuLastViewportCenterDate = planViewRef.current.instance.viewportCenterDate;
        }}
        onBeforeEventResize={canResizeOrDragEvent}
        onBeforeEventDrag={canResizeOrDragEvent}
        onBeforeEventDropFinalize={canDropEvent}
        onBeforeDragCreateFinalize={onCreateNewTaskEventByDragCreate}
        onEventDrop={onEventDragEndHandler}
        onEventResizeEnd={onEventDragEndHandler}
        onHeaderMenuItem={({ source, item, point }) => {
          if (item._menuId === "task") {
            const newRecord = source.store.createRecord();
            editorHandler.openEditor(EDITOR_KEYS.taskEditor, newRecord, {
              task: newRecord
            });
          } else if (item._menuId === "taskFinder") {
            editorHandler.openEditor(EDITOR_KEYS.taskFinderEditor, null, {
              point,
              onTaskClick: ({ record }) => {
                const originalRecord = getOriginalRecord(record);
                entityManager.openEntityManager({entityType: ENTITY_TYPES.TASK, entityId: originalRecord.id});
              }
            }, {
              by: "recordType",
              useExisting: true
            });
          }
        }}
        searchFeature={true}
      />
    </>
  );
};

export default PlanPage;
